import { Alert, Box, Chip, Tooltip } from "@mui/material";
import { ItemUsage } from "@models/catalog.model";

// Usage is shown where the rename decision happens — a chip on the browser row and a panel in
// the edit dialog — never on a separate page nobody visits first (MPN-1781). The tier colours
// follow the server's verdict: rules = contract/audience rules get rewritten, referenced = config and
// data the fan-out re-keys, unreferenced = free. Only an entity-type code change is still refused.

function orgCount(u: ItemUsage): number {
  const ids = new Set<number>();
  u.rules.forEach((r) => ids.add(r.org_id));
  u.configs.forEach((c) => { if (c.org_id != null) ids.add(c.org_id); });
  u.data.forEach((d) => ids.add(d.org_id));
  return ids.size;
}

function plural(n: number, word: string) { return `${n} ${word}${n === 1 ? "" : "s"}`; }

function detailLines(u: ItemUsage): string[] {
  return [
    ...u.rules.map((r) => `${r.org_name} · contract ${r.contract_id}`),
    ...u.audiences.map((a) => `audience: ${a}`),
    ...u.configs.map((c) => `config: ${c.ingest_definition_name}`),
    ...u.data.map((d) => `${d.org_name}: ${d.rows.toLocaleString()} rows`),
    ...u.metrics.map((m) => `metric: ${m}`),
  ];
}

export function UsageChip({ usage }: { usage?: ItemUsage }) {
  if (!usage) return null;
  const orgs = plural(orgCount(usage), "org");
  const chip =
    usage.tier === "rules"
      ? { label: `${orgs} · ${plural(usage.rules.length + usage.audiences.length, "rule")}`, color: "warning" as const }
      : usage.tier === "referenced"
        ? { label: `${orgs} · config/data`, color: "info" as const }
        : { label: "Unused", color: "default" as const };
  const lines = detailLines(usage);
  return (
    <Tooltip title={lines.length ? <Box component="ul" sx={{ m: 0, pl: 2 }}>{lines.map((l) => <li key={l}>{l}</li>)}</Box> : ""}>
      <Chip label={chip.label} color={chip.color} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
    </Tooltip>
  );
}

// Notice for a rename that the fan-out carries into rule text: which contracts get rewritten.
export function rewriteNotice(u: ItemUsage, target: string): string {
  const contracts = new Set(u.rules.map((r) => r.contract_id)).size;
  const parts = [
    contracts ? `conversion rules on ${plural(contracts, "contract")}` : "",
    u.audiences.length ? `${plural(u.audiences.length, "audience rule")}` : "",
  ].filter(Boolean);
  const what = parts.join(" and ");
  return `${what.charAt(0).toUpperCase()}${what.slice(1)} will be rewritten to ${target} when you save.`;
}

export function rekeyNotice(u: ItemUsage): string {
  const rows = u.data.reduce((n, d) => n + d.rows, 0);
  return `${rows.toLocaleString()} loaded row(s) across ${plural(u.data.length, "org")} will be re-keyed.`;
}

export function UsagePanel({
  usage, blockedReason, notice, footer,
}: { usage: ItemUsage; blockedReason?: string; notice?: string; footer?: string }) {
  const lines = detailLines(usage);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {blockedReason && <Alert severity="warning">{blockedReason}</Alert>}
      {!blockedReason && notice && <Alert severity="info">{notice}</Alert>}
      <Box sx={{ fontSize: 12, color: "#555" }}>
        {lines.length === 0 ? (
          "Not referenced anywhere — safe to rename."
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: 160, overflowY: "auto" }}>
            {lines.map((l) => <li key={l}>{l}</li>)}
          </Box>
        )}
      </Box>
      {footer && <Box sx={{ fontSize: 12, color: "#555" }}>{footer}</Box>}
    </Box>
  );
}
